import React, { useState, useEffect, useRef } from 'react';
import { Controller, Control, UseFormSetValue, FieldErrors, useWatch } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { format, subMonths, isValid, parseISO } from 'date-fns';
import { DateRangePicker, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useMediaQuery } from "../../hooks/useMediaQuery"; // Assuming this custom hook exists

// Define the shape of the form's filter object for type safety
interface FilterFormFields {
    filter: {
        property_id: string;
        start_date: string;
        end_date: string;
        room_type_id?: string;
    };
}

interface DateRangePickerProps {
    control: Control<FilterFormFields>;
    errors: FieldErrors<FilterFormFields>;
    setValue: UseFormSetValue<FilterFormFields>;
}

const DateRangePickerComponentV2: React.FC<DateRangePickerProps> = ({ control, errors, setValue }) => {
    // --- UI State and Hooks ---
    const isMobile = useMediaQuery('(max-width: 768px)');
    const calendarRef = useRef<HTMLDivElement>(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    // Watch the end_date to construct the full date range for the picker
    const endDateValue = useWatch({ control, name: 'filter.end_date' });

    // --- Effects ---

    // Set default form values on the initial render (Functionality from V1)
    useEffect(() => {
        const today = new Date();
        const oneMonthAgo = subMonths(today, 1);
        setValue("filter.start_date", format(oneMonthAgo, "yyyy-MM-dd"));
        setValue("filter.end_date", format(today, "yyyy-MM-dd"));
    }, [setValue]); // Runs once on mount as setValue is stable

    // Close the calendar when clicking outside of it (UI enhancement from V2)
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [calendarRef]);

    // --- Styles for the Calendar Popup ---
    const calendarContainerStyle: React.CSSProperties = {
        position: 'absolute',
        zIndex: 1000,
        top: '100%',
        marginTop: '8px',
        border: '1px solid #dfe2e5',
        borderRadius: '8px',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
        right: 0,
    };
    if (isMobile) {
        calendarContainerStyle.left = '50%';
        calendarContainerStyle.transform = 'translateX(-50%)';
        calendarContainerStyle.right = 'auto';
    }

    return (
        <>
            <Form.Label>Select Range:</Form.Label>
            <div className="position-relative" ref={calendarRef}>
                {/* Controller now wraps the entire logic for robust state management */}
                <Controller
                    name="filter.start_date"
                    control={control}
                    render={({ field }) => {
                        const startDate = field.value ? parseISO(field.value) : null;
                        const endDate = endDateValue ? parseISO(endDateValue) : null;

                        const displayValue = (startDate && endDate && isValid(startDate) && isValid(endDate))
                            ? `${format(startDate, 'dd MMM yyyy')} - ${format(endDate, 'dd MMM yyyy')}`
                            : 'Select date range';

                        const handleDateChange = (rangesByKey: RangeKeyDict) => {
                            const { selection } = rangesByKey;
                            if (selection.startDate && selection.endDate) {
                                // Update start_date using the Controller's onChange
                                field.onChange(format(selection.startDate, 'yyyy-MM-dd'));
                                // Update end_date directly using setValue
                                setValue('filter.end_date', format(selection.endDate, 'yyyy-MM-dd'), { shouldValidate: true });
                                // Close the picker after a range is selected for better UX
                                setIsCalendarOpen(false);
                            }
                        };

                        return (
                            <>
                                <div className="input-group">
                                    <Form.Control
                                        type='text'
                                        readOnly
                                        onClick={() => setIsCalendarOpen(o => !o)}
                                        value={displayValue}
                                        placeholder="Select date range"
                                        isInvalid={!!errors?.filter?.start_date || !!errors?.filter?.end_date}
                                        ref={field.ref} // Connect ref for form state subscription
                                    />
                                    <span className="input-group-text" onClick={() => setIsCalendarOpen(o => !o)} style={{ cursor: 'pointer' }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"></path><path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path><path d="M16 3l0 4"></path><path d="M8 3l0 4"></path><path d="M4 11l16 0"></path><path d="M8 15h2v2h-2z"></path></svg>
                                    </span>
                                </div>

                                {isCalendarOpen && (
                                    <div style={calendarContainerStyle}>
                                        <DateRangePicker
                                            onChange={handleDateChange}
                                            moveRangeOnFirstSelection={false}
                                            ranges={(startDate && endDate && isValid(startDate) && isValid(endDate)) ? [{ startDate, endDate, key: 'selection' }] : []}
                                            months={isMobile ? 1 : 2}
                                            direction={isMobile ? 'vertical' : 'horizontal'}
                                            inputRanges={[]}
                                            staticRanges={[]}
                                        />
                                    </div>
                                )}

                                {/* Unified error message display */}
                                {(errors?.filter?.start_date || errors?.filter?.end_date) &&
                                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                        {errors?.filter?.start_date?.message as string || errors?.filter?.end_date?.message as string}
                                    </Form.Control.Feedback>
                                }
                            </>
                        );
                    }}
                />
            </div>
        </>
    );
};

export default DateRangePickerComponentV2;